import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  enableProdMode,
  ErrorHandler,
  importProvidersFrom,
} from '@angular/core';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { environment } from './environments/environment.prod';
import { environment as appEnvironment } from './environments/environment';
import { AuthInterceptor } from 'app/core/interceptors/auth-interceptor';
import { AuthService } from 'app/modules/auth/services/auth.service';
import { provideToastr } from 'ngx-toastr';
import { CustomTitleStrategy } from 'app/core/services/title-strategy.service';
import { TitleStrategy } from '@angular/router';
import {
  provideCacheableAnimationLoader,
  provideLottieOptions,
} from 'ngx-lottie';
import player from 'lottie-web';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

Sentry.init({
  dsn: appEnvironment.sentryDsn,
  environment: appEnvironment.envName,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.2,
});

if (environment.production) {
  enableProdMode();
  //show this warning only on prod mode
  if (window) {
    selfXSSWarning();
  }
}

bootstrapApplication(AppComponent, {
  providers: [
    provideLottieOptions({ player: () => player }),
    provideCacheableAnimationLoader(),
    importProvidersFrom(BrowserModule, AppRoutingModule),
    provideAnimations(),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-bottom-right',
      preventDuplicates: true,
      progressBar: false,
      tapToDismiss: true,
      closeButton: true,
      newestOnTop: true,
    }),
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: TitleStrategy, useClass: CustomTitleStrategy },
    { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
    Sentry.TraceService,
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      // Exchange a pre-cookie stored token for an httpOnly cookie before
      // anything else calls the API.
      provide: APP_INITIALIZER,
      useFactory: (authService: AuthService) => () => authService.migrateLegacyToken(),
      deps: [AuthService],
      multi: true,
    },
  ],
}).catch((err) => {
  Sentry.captureException(err);
  console.error(err);
});

function selfXSSWarning() {
  setTimeout(() => {
    console.log(
      '%c** STOP **',
      'font-weight:bold; font: 2.5em Arial; color: white; background-color: #e11d48; padding-left: 15px; padding-right: 15px; border-radius: 25px; padding-top: 5px; padding-bottom: 5px;'
    );
    console.log(
      `\n%cThis is a browser feature intended for developers. Using this console may allow attackers to impersonate you and steal your information sing an attack called Self-XSS. Do not enter or paste code that you do not understand.`,
      'font-weight:bold; font: 2em Arial; color: #e11d48;'
    );
  });
}
